import styled from "styled-components";
import { Button } from "@interstate/components/Button";
import { CheckBox } from "@interstate/components/CheckBox";
import { useEffect, useRef, useState } from "react";
import {
  PostCrmGenAIContentStreamRequestPayload,
  postCrmGenAIContentStream
} from "@coxauto-ui/cxm/communications/api";
import {
  ConversationTypeEnum,
  Environment,
  SystemOfRecord
} from "@coxauto-ui/cxm/communications/interfaces";
import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { CenteredSpinner } from "@coxauto-ui/core-components";
import { formatContentForDisplay } from "@coxauto-ui/cxm/communications/util";

const SUBJECT_OVERWRITE_PREFERENCE_KEY = "genai-subject-overwrite-preference";

/* eslint-disable-next-line */
export interface GenerativeAIResponseCardProps {
  activeListTab: ConversationTypeEnum;
  channelType: ConversationTypeEnum;
  customerId: string;
  customerSystem: SystemOfRecord;
  env: Environment;
  index: number;
  prompt: string;
  responseMessages: string[];
  responseSubject: string;
  sessionId: string;
  insertGeneratedResponse: (
    response: string,
    subject?: string,
    shouldOverwriteSubject?: boolean
  ) => void;
  setSessionId: (sessionId: string) => void;
  setResponseMessages: (message: string, index: number) => void;
  setResponseSubject: (
    subject: string,
    index: number,
    append?: boolean
  ) => void;
}

const StyledGenerativeAIResponseCard = styled.div`
  padding: 16px;
  background-color: #fff;
  border: 1px solid #cacfd9;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .generative-ai-response-card-subject {
    padding: 12px;
    background-color: #f5f6f7;
    border-radius: 4px;
    margin-bottom: 8px;

    .subject-label {
      font-weight: 600;
      margin-bottom: 4px;
      color: #333;
    }

    .subject-value {
      color: #333;
    }
  }

  .generative-ai-response-card-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: flex-end;

    .subject-overwrite-checkbox {
      flex: 1;
      display: flex;
      align-items: center;
    }
  }

  .generative-ai-response-card-insert-button {
    flex: 0 0 auto;
    width: min-content;
  }

  .generative-ai-response-card-prompt {
    font-style: italic;

    .prompt-label {
      margin-bottom: 8px;
      font-weight: 600;
    }

    .prompt-content {
      padding: 12px;
      background-color: #f5f6f7;
      border-radius: 4px;
      border: 1px solid #e0e0e0;

      p {
        margin: 0 0 1em 0;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .suggested-text-label {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    font-style: italic;
  }
`;

const userFriendlyErrorMessage =
  "Unfortunately, there was an issue with the last prompt. Please try again. If the problem continues, please contact your support team.";

export function GenerativeAIResponseCard({
  activeListTab,
  channelType,
  customerId,
  customerSystem,
  env,
  index,
  prompt,
  responseMessages,
  responseSubject,
  sessionId,
  setResponseMessages,
  setResponseSubject,
  setSessionId,
  insertGeneratedResponse
}: GenerativeAIResponseCardProps) {
  const [error, setError] = useState("");
  const [isInProgress, setIsInProgress] = useState(false);
  const [shouldOverwriteSubject, setShouldOverwriteSubject] = useState(() => {
    const stored = localStorage.getItem(SUBJECT_OVERWRITE_PREFERENCE_KEY);
    return stored !== null ? stored === "true" : true; // Default to true
  });
  const streamStarted = useRef(false);
  const contentReceived = useRef(false);

  useEffect(() => {
    if (streamStarted.current) {
      return;
    }

    streamStarted.current = true;

    (async function () {
      const payload: PostCrmGenAIContentStreamRequestPayload = {
        consumerRecordSystem: customerSystem,
        consumerId: customerId,
        channelType,
        input: prompt
      };

      if (sessionId) {
        payload.sessionId = sessionId;
      }

      const onClose = () => {
        setIsInProgress(false);

        // Check if we received any content after stream closes
        // If no content was received, set an error
        if (!contentReceived.current) {
          setError(userFriendlyErrorMessage);
        }
      };

      const onError = (error: Error) => {
        throw error;
      };

      const onMessage = (ev: EventSourceMessage) => {
        if (ev.event === "Content") {
          contentReceived.current = true;
          setResponseMessages(ev.data.replace(/\\n/g, "\n"), index);
        } else if (ev.event === "SessionId") {
          setSessionId(ev.data);
        } else if (ev.event === "Subject") {
          setResponseSubject(ev.data, index, true); // true to append
        }
      };

      try {
        setIsInProgress(true);
        await postCrmGenAIContentStream({
          payload,
          env,
          onclose: onClose,
          onerror: onError,
          onmessage: onMessage
        });
      } catch (error: unknown) {
        setIsInProgress(false);
        setError(
          error instanceof Error ? error.message : "Sorry, something went wrong"
        );
      }
    })();
    // not sure about the dep list... might remove it
  }, [
    channelType,
    customerId,
    customerSystem,
    env,
    index,
    prompt,
    sessionId,
    setResponseMessages,
    setResponseSubject,
    setSessionId
  ]);

  const onInsertGeneratedResponseClick = () => {
    const normalizedContent = responseMessages.join("");

    // Save the checkbox preference to localStorage
    localStorage.setItem(
      SUBJECT_OVERWRITE_PREFERENCE_KEY,
      shouldOverwriteSubject.toString()
    );

    // Only apply HTML formatting for email channel type (Kendo WYSIWYG editor)
    // Text and other channel types use plain text editors
    if (channelType === ConversationTypeEnum.EMAIL) {
      const htmlFormattedContent = formatContentForDisplay(normalizedContent);
      insertGeneratedResponse(
        htmlFormattedContent,
        responseSubject,
        shouldOverwriteSubject
      );
    } else {
      // For text and other non-HTML editors, insert plain text with normalized line breaks
      const plainTextContent = normalizedContent
        .replace(/\r\n/g, "\n") // Convert Windows line endings to Unix
        .replace(/\r/g, "\n") // Convert old Mac line endings to Unix
        .trim(); // Remove leading/trailing whitespace
      insertGeneratedResponse(
        plainTextContent,
        responseSubject,
        shouldOverwriteSubject
      );
    }
  };

  const onCheckboxChange = () => {
    setShouldOverwriteSubject(prev => !prev);
  };

  return (
    <StyledGenerativeAIResponseCard>
      {prompt && index === 0 && (
        <div
          className="generative-ai-response-card-prompt"
          data-testid={`generative-ai-response-card-prompt-${activeListTab}-${index}`}
          id={`generative-ai-response-card-prompt-${activeListTab}-${index}`}
        >
          <div className="prompt-label">Original Text:</div>
          <div className="prompt-content">{prompt}</div>
        </div>
      )}
      {error && (
        <div
          className="generative-ai-response-card-error-wrapper"
          data-rawerrormessage={error}
          data-testid={`generative-ai-response-card-error-wrapper-${activeListTab}-${index}`}
          id={`generative-ai-response-card-error-wrapper-${activeListTab}-${index}`}
        >
          {userFriendlyErrorMessage}
        </div>
      )}
      {responseMessages.length === 0 && isInProgress && (
        <CenteredSpinner
          data-testid={`generative-ai-waiting-for-response-loading-indicator-${activeListTab}-${index}`}
          id={`generative-ai-waiting-for-response-loading-indicator-${activeListTab}-${index}`}
        />
      )}
      {prompt && index === 0 && responseMessages.length > 0 && (
        <div
          className="suggested-text-label"
          data-testid={`generative-ai-suggested-text-label-${activeListTab}-${index}`}
          id={`generative-ai-suggested-text-label-${activeListTab}-${index}`}
        >
          Suggested Text:
        </div>
      )}
      {responseSubject && channelType === ConversationTypeEnum.EMAIL && (
        <div
          className="generative-ai-response-card-subject"
          data-testid={`generative-ai-response-card-subject-${activeListTab}-${index}`}
          id={`generative-ai-response-card-subject-${activeListTab}-${index}`}
        >
          <div className="subject-label">Email Subject:</div>
          <div className="subject-value">{responseSubject}</div>
        </div>
      )}
      {responseMessages && (
        <div
          className="generative-ai-response-card-generated-response"
          data-testid={`generative-ai-response-card-generated-response-${activeListTab}-${index}`}
          id={`generative-ai-response-card-generated-response-${activeListTab}-${index}`}
        >
          {channelType === ConversationTypeEnum.EMAIL ? (
            // For email channel, display with HTML formatting to match what will be inserted
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: formatContentForDisplay(responseMessages.join(""))
              }}
            />
          ) : (
            // For text and other channels, display plain text
            responseMessages
              .join("")
              .replace(/\r\n/g, "\n")
              .replace(/\r/g, "\n")
              .trim()
          )}
        </div>
      )}
      <div className="generative-ai-response-card-actions">
        {responseSubject && channelType === ConversationTypeEnum.EMAIL && (
          <CheckBox
            checked={shouldOverwriteSubject}
            className="subject-overwrite-checkbox"
            data-testid={`generative-ai-response-card-subject-checkbox-${activeListTab}-${index}`}
            id={`generative-ai-response-card-subject-checkbox-${activeListTab}-${index}`}
            label="Use Email Subject"
            name={`subject-overwrite-${activeListTab}-${index}`}
            value=""
            onChange={onCheckboxChange}
          />
        )}
        <Button
          className="generative-ai-response-card-insert-button"
          data-testid={`generative-ai-response-card-insert-button-${activeListTab}-${index}`}
          disabled={isInProgress || responseMessages.length === 0}
          id={`generative-ai-response-card-insert-button-${activeListTab}-${index}`}
          onClick={onInsertGeneratedResponseClick}
        >
          Insert Text
        </Button>
      </div>
    </StyledGenerativeAIResponseCard>
  );
}
